import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Pagination,
  Popover,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Receipt,
  CurrencyRupee,
  TrendingUp,
  Download,
  Visibility,
  LocalAtm,
  ChevronLeft,
  ChevronRight,
  Close,
  EditOutlined,
  Block,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import { isStudioScopedRole } from '../utils/roles';

export interface Transaction {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
    phoneNumber?: string;
  };
  booking?: {
    _id: string;
    bookingId: string;
    studio: {
      studioName: string;
      isGstRegistered?: boolean;
    };
    event?: {
      eventName: string;
    };
    session?: {
      className: string;
    };
  };
  purchasedPack?: {
    _id: string;
    title: string;
    purchaseSource?: string;
    pack?: {
      title: string;
    };
    studio?: {
      studioName: string;
      isGstRegistered?: boolean;
      whitelabelConfig?: {
        appSource?: string;
      };
    };
    studioName?: string;
    packTitle?: string;
  };
  modeOfPayment: 'online' | 'cash' | 'subscription' | 'free';
  basePrice: number;
  taxes: number;
  paymentId?: string;
  isFromWebsite?: boolean;
  bookingSource?: string;
  isExternalPayment?: boolean;
  // Set by the backend: not paid yet, so left out of revenue.
  isPaymentPending?: boolean;
  // Set by the backend: the invoice no longer stands, so also out of revenue.
  isInvoiceCancelled?: boolean;
  // Set by the backend: the money landed on Sweat Social's Razorpay, not the studio's.
  isReceivedBySweatSocial?: boolean;
  // Only on a row a studio owner has edited.
  originalAmount?: number;
  originalModeOfPayment?: string;
  createdAt: string;
  updatedAt: string;
}

// Razorpay rows feed payouts and pack credits / comps have no payment, so only
// money the studio collected itself can be edited. The backend enforces this too.
const isRazorpayPayment = (t: Transaction): boolean =>
  Boolean(t.isExternalPayment) ||
  (t.paymentId || '').startsWith('pay_') ||
  ((t.booking as any)?.paymentId || '').startsWith('pay_');

const isLivePayment = (t: Transaction): boolean =>
  !t.isInvoiceCancelled &&
  (t.modeOfPayment === 'cash' || t.modeOfPayment === 'online') &&
  Boolean(t.booking || t.purchasedPack);

const isStudioCollected = (t: Transaction): boolean => isLivePayment(t) && !isRazorpayPayment(t);

const isTransactionEditable = (t: Transaction): boolean =>
  isStudioCollected(t) && (t.booking as any)?.bookingStatus !== 'CANCELLED';

// Whatever the booking's status. Cancelling a pending invoice also clears the debt.
const isInvoiceCancellable = isStudioCollected;

const transactionStudioId = (t: Transaction): any =>
  (t.booking?.studio as any)?._id || (t.purchasedPack?.studio as any)?._id;

interface Studio {
  _id: string;
  studioName: string;
  whitelabelConfig?: { appSource?: string };
}

export interface StudioStat {
  studioId: string;
  studioName: string;
  appSource: string;
  ownGatewayNet: number;
  ownGatewayTax: number;
  ownGatewayCount: number;
  sweatSocialGatewayNet: number;
  sweatSocialGatewayTax: number;
  sweatSocialGatewayCount: number;
  totalNet: number;
  totalTax: number;
  totalCount: number;
  // Cash/manual for this studio — own-app vs booked through a Sweat Social
  // channel, same split the Razorpay gateway figures above use.
  ownAppCashRevenue: number;
  ownAppCashCount: number;
  ssRoutedCashRevenue: number;
  ssRoutedCashCount: number;
}

export interface SourceStat {
  source: string;
  netRevenue: number;
  tax: number;
  count: number;
}

// One studio's slice of the All-tab cash / manual total: cash taken in the
// studio's own app vs cash for its classes booked through a Sweat Social channel.
// No-gateway partner apps (Laya / Matchday / Ember & Ice) are listed here too,
// keyed by `source` instead of `studioId` — for cash the gateway distinction
// doesn't apply, so they're not a separate section.
interface CashStudioStat {
  studioId: string | null;
  studioName: string | null;
  source: string | null;
  ownCashRevenue: number;
  ownCashCount: number;
  ssRoutedCashRevenue: number;
  ssRoutedCashCount: number;
  totalCashRevenue: number;
  totalCashCount: number;
}

// Cash / manual booked through one Sweat Social channel (app / website / admin).
interface CashSourceStat {
  source: string;
  cashRevenue: number;
  cashCount: number;
}

interface CashManualBreakdown {
  studios: CashStudioStat[];
  sweatSocial: { cashRevenue: number; cashCount: number; bySource?: CashSourceStat[] };
}

export interface TransactionStats {
  sweatSocial: ({ netRevenue: number; tax: number; count: number; bySource?: SourceStat[]; cashRevenue: number; cashCount: number; cashTax: number }) | null;
  studios: StudioStat[];
  // captureCount: exact Razorpay payments behind netRevenue (count mixes in
  // cash and, globally, cancelled/free rows). Absent on an older backend.
  grandTotal: { netRevenue: number; tax: number; count: number; captureCount?: number };
  // Period-wide (not just the currently-loaded page), scoped the same way as
  // grandTotal: whole-period total on the All/Sweat Social tabs, that one
  // studio's total on a whitelabel-studio tab. `breakdown` is only sent on the
  // All tab — a particular studio's tab just shows that studio's cash total.
  // `tax` is the GST actually recorded within `total` (cash was never
  // reliably GST-split, but plenty of rows do carry real tax) — not shown on
  // the Cash/Manual card itself, only used by callers building a true
  // net-of-GST combined total (Dashboard).
  cashManual: { total: number; count: number; tax: number; breakdown?: CashManualBreakdown };
  // Money EXCLUDED from every figure above because it paid for a booking that
  // was later cancelled with no refund on record (see getTransactionStats'
  // _isCancelledBooking on the backend). Disclosed, not netted in, so a
  // viewer can see why this total differs from the raw Transactions list.
  cancelled: { gross: number; count: number };
}

const PAGE_SIZE = 25;

const IST_TIMEZONE = 'Asia/Kolkata';
const istDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: IST_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
/** 'YYYY-MM-DD' in IST. */
const toISTDateKey = (d: Date): string => istDateFormatter.format(d);
const getTodayISTDateKey = () => toISTDateKey(new Date());
/** 'YYYY-MM' in IST. */
const getCurrentMonthKey = () => getTodayISTDateKey().slice(0, 7);
// IST-aware full ISO strings so the backend date range matches IST midnight exactly.
const toISTStart = (yyyy: string, mm: string, dd: string) => `${yyyy}-${mm}-${dd}T00:00:00+05:30`;
const toISTEnd = (yyyy: string, mm: string, dd: string) => `${yyyy}-${mm}-${dd}T23:59:59.999+05:30`;
const monthLabel = (monthKey: string) => {
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};
// Full month range, clamped to today when the month IS the current one — "this
// month" means "so far", not a false future range.
const computeMonthRange = (monthKey: string): { startDate: string; endDate: string } => {
  const [year, month] = monthKey.split('-');
  const lastDay = new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
  const isCurrentMonth = monthKey === getCurrentMonthKey();
  const endDay = isCurrentMonth ? getTodayISTDateKey().split('-')[2] : String(lastDay).padStart(2, '0');
  return {
    startDate: toISTStart(year, month, '01'),
    endDate: toISTEnd(year, month, endDay),
  };
};

// Pack-purchase transactions store the full gross amount in basePrice with
// taxes=0. For a GST-registered studio that hides the GST. Re-split the
// inclusive 5% GST for display, mirroring the backend receipt + revenue logic.
// Booking transactions (taxes already > 0) and non-GST studios are unchanged.
export function resolveGst(t: Transaction): { base: number; tax: number; total: number } {
  const base = t.basePrice || 0;
  const tax = t.taxes || 0;
  const total = Math.round((base + tax) * 100) / 100;
  const studio = t.purchasedPack?.studio || t.booking?.studio;
  const isGstRegistered = studio?.isGstRegistered !== false;
  if (tax > 0 || !isGstRegistered || base <= 0) return { base, tax, total };
  const splitBase = Math.round((base / 1.05) * 100) / 100;
  return { base: splitBase, tax: Math.round((total - splitBase) * 100) / 100, total };
}

// For pack purchases: source = purchasedPack.purchaseSource (null → treat as Sweat Social).
// For booking transactions: source = bookingSource.
export function effectiveTransactionSource(t: Transaction): string | undefined {
  if (t.purchasedPack) {
    return t.purchasedPack.purchaseSource || undefined;
  }
  return t.bookingSource || undefined;
}

export function isSweatSocialTransaction(t: Transaction): boolean {
  const src = effectiveTransactionSource(t);
  return (!src || src === 'mobile_app' || src === 'sweatsocial' || src === 'website' || src === 'admin') && !t.isExternalPayment;
}

export function sourceLabel(src?: string): string {
  if (!src || src === 'mobile_app' || src === 'sweatsocial') return 'Sweat Social';
  if (src === 'website') return 'Website';
  if (src === 'admin') return 'Admin';
  return src.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const sweatSocialSourceLabel = (src: string): string => {
  if (src === 'app') return 'Mobile App';
  if (src === 'website') return 'Website';
  if (src === 'admin') return 'Admin';
  return sourceLabel(src);
};

const SWEAT_SOCIAL_SOURCE_ORDER = ['app', 'website', 'admin'] as const;

// Always returns all three SS channels in a fixed order (App, Website, Admin),
// filling in a zero row for whichever ones had no transactions this period —
// so "no admin revenue" reads as an explicit ₹0 row instead of silently
// vanishing, same for Website/App.
const zeroFillSweatSocialSources = (bySource?: SourceStat[]): SourceStat[] => {
  const bySrc = new Map((bySource || []).map((r) => [r.source, r]));
  return SWEAT_SOCIAL_SOURCE_ORDER.map((source) => bySrc.get(source) || { source, netRevenue: 0, tax: 0, count: 0 });
};

const SWEAT_SOCIAL_CHANNEL_COLORS: Record<(typeof SWEAT_SOCIAL_SOURCE_ORDER)[number], string> = {
  app: colors.primary,
  website: colors.info,
  admin: colors.warning,
};

const SweatSocialSourceRows: React.FC<{ bySource?: SourceStat[] }> = ({ bySource }) => {
  const channels = zeroFillSweatSocialSources(bySource);
  const total = channels.reduce((sum, row) => sum + row.netRevenue, 0);
  const activeCount = channels.filter((row) => row.netRevenue > 0).length;

  return (
    <Box sx={{ mt: 1.25 }}>
      <Box sx={{ display: 'flex', height: 6, borderRadius: 4, overflow: 'hidden', width: '100%', backgroundColor: `${colors.divider}99` }}>
        {channels.map((row) => {
          const pct = total > 0 ? (row.netRevenue / total) * 100 : 100 / channels.length;
          const active = row.netRevenue > 0;
          return (
            <Box
              key={row.source}
              title={`${sweatSocialSourceLabel(row.source)}: ${formatINR(row.netRevenue)}${total > 0 && active ? ` (${Math.round(pct)}%)` : ''}`}
              sx={{
                flexGrow: 0,
                flexShrink: 0,
                flexBasis: `${pct}%`,
                // A channel that contributed anything at all stays visible even
                // when it's a sliver next to a dominant one — otherwise the bar
                // reads as "only one channel all month".
                minWidth: active && activeCount > 1 ? 10 : 0,
                backgroundColor: SWEAT_SOCIAL_CHANNEL_COLORS[row.source as typeof SWEAT_SOCIAL_SOURCE_ORDER[number]],
                opacity: active ? 1 : 0.25,
                borderRight: active && activeCount > 1 ? '1.5px solid #fff' : 'none',
                transition: 'flex-basis 0.2s ease',
              }}
            />
          );
        })}
      </Box>
      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={0.75} mt={0.75}>
        {channels.map((row) => {
          const pct = total > 0 ? (row.netRevenue / total) * 100 : 0;
          return (
            <Box key={row.source} minWidth={0}>
              <Typography
                variant="caption"
                fontWeight={700}
                display="block"
                sx={{ color: SWEAT_SOCIAL_CHANNEL_COLORS[row.source as typeof SWEAT_SOCIAL_SOURCE_ORDER[number]], fontSize: '0.68rem', lineHeight: 1.3 }}
              >
                {sweatSocialSourceLabel(row.source)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.3 }}>
                {formatINR(row.netRevenue)}
                {row.netRevenue > 0 && total > 0 && ` · ${Math.round(pct)}%`}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export const formatINR = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export const getPaymentModeColor = (mode: string) => {
  switch (mode) {
    case 'online': return colors.success;
    case 'cash': return colors.warning;
    // Same colours UserDetailPage's PAYMENT_MODE_CONFIG already uses for
    // these two modes, rather than lumping them into a generic grey default.
    case 'subscription': return '#6A1B9A';
    case 'free': return '#78909C';
    default: return colors.textSecondary;
  }
};

// The split-bar segments a stats breakdown popup can drill into. Each maps 1:1
// to a row-classification in the backend's getStatsSegmentTransactions — the
// first four keyed by studio, the next two by partner-app source, and the two
// sweatSocialDirect* buckets (global, no studioId) for the "Sweat Social
// (direct)" section of the Razorpay popup (captures) and the Cash/Manual popup
// (cash) respectively — optionally narrowed to one channel via `source`.
export type SegmentBucket =
  | 'ownGateway'
  | 'sweatSocialGateway'
  | 'ownAppCash'
  | 'ssRoutedCash'
  | 'partnerInApp'
  | 'partnerViaSweatSocial'
  | 'sweatSocialDirect'
  | 'sweatSocialDirectCash'
  // Studio-scoped, merged-row equivalents of the four studio buckets above —
  // studioRazorpayAll = ownGateway + sweatSocialGateway, studioCashAll =
  // ownAppCash + ssRoutedCash. Used by Dashboard's compact Total Revenue
  // card, which shows one Razorpay row and one Cash row per studio instead
  // of splitting by channel.
  | 'studioRazorpayAll'
  | 'studioCashAll'
  // The "N cancelled" disclosure's own drill-down — global (All-tab/super
  // admin) when no studioId is given, scoped when one is.
  | 'cancelled'
  // Super admin Dashboard: every Razorpay / Cash payment behind the
  // platform-wide totals (Sweat Social + whitelabel own-app + partner apps).
  | 'allRazorpay'
  | 'allCash';

export interface SegmentSelection {
  studioId?: string;
  source?: string;
  studioName: string;
  bucket: SegmentBucket;
  bucketLabel: string;
  count: number;
}

const POPUP_WIDTH = 360;
const POPUP_BODY_MAX_HEIGHT = 360;

const popoverPaperSx = {
  pointerEvents: 'auto' as const,
  ml: 1.5,
  borderRadius: 2,
  border: `1px solid ${colors.divider}`,
  boxShadow: '0 8px 32px rgba(244, 143, 180, 0.22)',
  overflow: 'hidden',
};

// Lets a descendant (a drill-down link) "pin" the surrounding HoverPopover open
// before it launches a dialog, so closing that dialog returns you to the popover
// instead of nothing. Null when rendered outside a HoverPopover.
const PinPopoverContext = React.createContext<(() => void) | null>(null);

export const HoverPopover: React.FC<{
  anchor: React.ReactNode;
  children: React.ReactNode;
  // Side of the anchor the popover opens on. 'left' for anchors near the
  // right edge of the screen (e.g. the last card in a row).
  placement?: 'right' | 'left';
}> = ({ anchor, children, placement = 'right' }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  // Pinned = kept open regardless of hover (a drill-down dialog is/was open on
  // top of it). Only a click-away or Esc dismisses a pinned popover.
  const [pinned, setPinned] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const open = hoverOpen || pinned;

  const handleEnter = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setHoverOpen(true);
  };

  const handleLeave = () => {
    // Never let a mouse-leave collapse a pinned popover — while a dialog is open
    // on top, the paper fires mouseleave immediately, which would otherwise hide
    // the popover behind it and leave nothing to return to.
    closeTimerRef.current = setTimeout(() => setHoverOpen(false), 150);
  };

  const pin = useCallback(() => setPinned(true), []);
  const dismiss = useCallback(() => {
    setPinned(false);
    setHoverOpen(false);
  }, []);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  return (
    <PinPopoverContext.Provider value={pin}>
      <Box
        ref={anchorRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        sx={{
          borderRadius: 2,
          transition: 'transform 0.15s ease',
          ...(open && { transform: 'translateY(-1px)' }),
        }}
      >
        {anchor}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: 'center', horizontal: placement }}
        transformOrigin={{ vertical: 'center', horizontal: placement === 'right' ? 'left' : 'right' }}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        onClose={dismiss}
        sx={{ pointerEvents: pinned ? 'auto' : 'none' }}
        slotProps={{
          paper: {
            onMouseEnter: handleEnter,
            onMouseLeave: handleLeave,
            elevation: 8,
            // Gap between card and popover goes on the side facing the card.
            sx: placement === 'right' ? popoverPaperSx : { ...popoverPaperSx, ml: 0, mr: 1.5 },
          },
        }}
      >
        {children}
      </Popover>
    </PinPopoverContext.Provider>
  );
};

export const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ElementType;
  iconColor?: string;
  footer?: React.ReactNode;
}> = ({
  label,
  value,
  icon: Icon,
  iconColor,
  footer,
}) => (
  <Card sx={{ backgroundColor: colors.white, border: `1px solid ${colors.divider}`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box flex={1} minWidth={0}>
          <Typography variant="body2" color="text.secondary" mb={0.5}>{label}</Typography>
          <Typography variant="h4" fontWeight={700} color={colors.textPrimary}>{value}</Typography>
          {footer}
        </Box>
        <Icon sx={{ fontSize: 48, color: iconColor || colors.textSecondary, opacity: 0.3, flexShrink: 0 }} />
      </Box>
    </CardContent>
  </Card>
);

// Compact two-segment bar: a studio's own Razorpay share vs Sweat Social's
// Razorpay share of that studio's total — the "clean distribution" visual
// used both in the "All" tab's hover and inline on a whitelabel studio's tab.
// A "· N txns" that turns into a clickable link when onClick is supplied —
// used to drill from a split-bar segment into that segment's transactions.
// `separator` defaults to " · " (it usually follows an amount); pass "" when
// the count leads its line. `unit` is [singular, plural] — defaults to txn/txns.
export const TxnCountText: React.FC<{
  count: number;
  onClick?: () => void;
  separator?: string;
  unit?: [string, string];
}> = ({ count, onClick, separator = ' · ', unit = ['txn', 'txns'] }) => {
  const pinPopover = useContext(PinPopoverContext);
  const text = `${count} ${count === 1 ? unit[0] : unit[1]}`;
  if (!onClick || count <= 0) return <>{`${separator}${text}`}</>;
  const activate = () => { pinPopover?.(); onClick(); };
  return (
    <>
      {separator}
      <Box
        component="span"
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); activate(); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); } }}
        sx={{
          color: colors.primary,
          fontWeight: 700,
          cursor: 'pointer',
          textDecoration: 'underline',
          textUnderlineOffset: 2,
          '&:hover': { opacity: 0.8 },
        }}
      >
        {text}
      </Box>
    </>
  );
};

// Compact two-segment bar: a studio's own Razorpay share vs Sweat Social's
// Razorpay share of that studio's total — the "clean distribution" visual
// used both in the "All" tab's hover and inline on a whitelabel studio's tab.
export const GatewaySplitBar: React.FC<{
  ownLabel: string;
  ownNet: number;
  sweatSocialNet: number;
  ownCount?: number;
  sweatSocialCount?: number;
  compact?: boolean;
  hideLegend?: boolean;
  onOwnClick?: () => void;
  onSweatSocialClick?: () => void;
}> = ({
  ownLabel,
  ownNet,
  sweatSocialNet,
  ownCount,
  sweatSocialCount,
  compact = false,
  hideLegend = false,
  onOwnClick,
  onSweatSocialClick,
}) => {
  const total = ownNet + sweatSocialNet;
  const ownPct = total > 0 ? (ownNet / total) * 100 : 50;
  const ownShortLabel = ownLabel.length > 18 ? `${ownLabel.slice(0, 16)}…` : ownLabel;
  return (
    <Box>
      <Box sx={{ display: 'flex', height: compact ? 6 : 8, borderRadius: 4, overflow: 'hidden', width: '100%', backgroundColor: `${colors.divider}99` }}>
        {/* Both sides stay visible when both contributed, even if one is a
            sliver — a solid single-colour bar wrongly reads as "one side only". */}
        <Box sx={{ flexGrow: 0, flexShrink: 0, flexBasis: `${ownPct}%`, minWidth: ownNet > 0 && sweatSocialNet > 0 ? 10 : 0, backgroundColor: colors.info, borderRight: ownNet > 0 && sweatSocialNet > 0 ? '1.5px solid #fff' : 'none', transition: 'flex-basis 0.2s ease' }} />
        <Box sx={{ flexGrow: 1, flexShrink: 1, flexBasis: `${100 - ownPct}%`, minWidth: ownNet > 0 && sweatSocialNet > 0 ? 10 : 0, backgroundColor: colors.primary, transition: 'flex-basis 0.2s ease' }} />
      </Box>
      {!hideLegend && (
        <Box sx={{ display: 'flex', flexDirection: compact ? 'column' : 'row', justifyContent: 'space-between', gap: compact ? 0.35 : 0, mt: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.info, mr: 0.5, verticalAlign: 'middle' }} />
            {ownShortLabel}: {formatINR(ownNet)}
            {ownCount != null && <TxnCountText count={ownCount} onClick={onOwnClick} />}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.primary, mr: 0.5, verticalAlign: 'middle' }} />
            Sweat Social: {formatINR(sweatSocialNet)}
            {sweatSocialCount != null && <TxnCountText count={sweatSocialCount} onClick={onSweatSocialClick} />}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const GatewaySourceRows: React.FC<{
  studioLabel: string;
  ownNet: number;
  sweatSocialNet: number;
}> = ({ studioLabel, ownNet, sweatSocialNet }) => (
  <Box sx={{ mt: 1.25 }}>
    <GatewaySplitBar
      ownLabel={studioLabel}
      ownNet={ownNet}
      sweatSocialNet={sweatSocialNet}
      compact
      hideLegend
    />
    <Box display="flex" justifyContent="space-between" gap={1} mt={0.75}>
      <Typography variant="caption" color="text.secondary" noWrap title={`${studioLabel} Razorpay`}>
        <Box component="span" sx={{ color: colors.info, fontWeight: 700 }}>Studio</Box>
        {' '}{formatINR(ownNet)}
      </Typography>
      <Typography variant="caption" color="text.secondary" textAlign="right">
        <Box component="span" sx={{ color: colors.primary, fontWeight: 700 }}>Sweat Social</Box>
        {' '}{formatINR(sweatSocialNet)}
      </Typography>
    </Box>
  </Box>
);

export const RevenueBreakdownPopup: React.FC<{
  stats: TransactionStats | null;
  periodLabel: string;
  isLoading?: boolean;
  onSegmentClick?: (sel: SegmentSelection) => void;
}> = ({ stats, periodLabel, isLoading, onSegmentClick }) => {
  if (isLoading && !stats) {
    return (
      <Box sx={{ width: POPUP_WIDTH, p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const hasStudios = (stats?.studios.length ?? 0) > 0;
  const hasSweatSocial = Boolean(stats?.sweatSocial);

  if (!stats || (!hasStudios && !hasSweatSocial)) {
    return (
      <Box sx={{ width: POPUP_WIDTH, p: 2.5 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={0.5}>Revenue breakdown</Typography>
        <Typography variant="body2" color="text.secondary">No Razorpay revenue in this period</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: POPUP_WIDTH, maxWidth: '92vw' }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: `${colors.primary}0D`, borderBottom: `1px solid ${colors.divider}` }}>
        <Typography variant="subtitle2" fontWeight={700} color={colors.textPrimary} lineHeight={1.3}>
          Revenue breakdown
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
          {periodLabel} · Razorpay only
        </Typography>
      </Box>

      <Box sx={{ px: 2, py: 1.25, maxHeight: POPUP_BODY_MAX_HEIGHT, overflowY: 'auto' }}>
        {hasStudios && (
          <>
            {/* A studio owner viewing their own Total Revenue card only ever
                sees their one row here — "Whitelabel studios" as a category
                label reads oddly for "my own studio's numbers", so it's only
                shown when there's an actual list of studios to categorise
                (the super-admin All-tab view). */}
            {stats.studios.length > 1 && (
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.6, textTransform: 'uppercase' }}>
                Whitelabel studios
              </Typography>
            )}
            {stats.studios.map((s, idx) => (
              <Box key={s.studioId} sx={{ py: 1.25, borderBottom: idx < stats.studios.length - 1 || hasSweatSocial ? `1px solid ${colors.divider}` : 'none' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.5}>
                  <Box minWidth={0}>
                    <Typography variant="body2" fontWeight={700} color={colors.textPrimary} noWrap title={s.studioName}>
                      {s.studioName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.totalCount} txn{s.totalCount !== 1 ? 's' : ''}
                      {s.totalTax > 0 && ` · Tax ${formatINR(s.totalTax)}`}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} color={colors.success} sx={{ flexShrink: 0 }}>
                    {formatINR(s.totalNet)}
                  </Typography>
                </Box>
                <GatewaySplitBar
                  ownLabel={s.studioName}
                  ownNet={s.ownGatewayNet}
                  sweatSocialNet={s.sweatSocialGatewayNet}
                  ownCount={s.ownGatewayCount}
                  sweatSocialCount={s.sweatSocialGatewayCount}
                  compact
                  onOwnClick={
                    onSegmentClick && s.ownGatewayCount > 0
                      ? () => onSegmentClick({ studioId: s.studioId, studioName: s.studioName, bucket: 'ownGateway', bucketLabel: 'Own gateway (Razorpay)', count: s.ownGatewayCount })
                      : undefined
                  }
                  onSweatSocialClick={
                    onSegmentClick && s.sweatSocialGatewayCount > 0
                      ? () => onSegmentClick({ studioId: s.studioId, studioName: s.studioName, bucket: 'sweatSocialGateway', bucketLabel: 'Sweat Social gateway (Razorpay)', count: s.sweatSocialGatewayCount })
                      : undefined
                  }
                />
              </Box>
            ))}
          </>
        )}

        {hasSweatSocial && stats.sweatSocial && (
          <Box sx={{ py: 1.25 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.6, textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
              Sweat Social (direct)
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
              <Box>
                <Typography variant="body2" fontWeight={600} color={colors.textPrimary}>All channels</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {/* The txn count itself is the drill-down link — opens the
                      Razorpay-captured rows behind this figure (cash / manual
                      excluded, so the list can be shorter than this count). */}
                  <TxnCountText
                    count={stats.sweatSocial.count}
                    separator=""
                    onClick={
                      onSegmentClick
                        ? () =>
                            onSegmentClick({
                              studioName: 'Sweat Social (direct)',
                              bucket: 'sweatSocialDirect',
                              bucketLabel: 'All channels',
                              count: stats.sweatSocial!.count,
                            })
                        : undefined
                    }
                  />
                  {stats.sweatSocial.tax > 0 && ` · Tax ${formatINR(stats.sweatSocial.tax)}`}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={700} color={colors.success} sx={{ flexShrink: 0 }}>
                {formatINR(stats.sweatSocial.netRevenue)}
              </Typography>
            </Box>
            <Box sx={{ pl: 1, borderLeft: `2px solid ${colors.primary}40`, mt: 0.75 }}>
              <SweatSocialSourceRows bySource={stats.sweatSocial.bySource} />
            </Box>
          </Box>
        )}
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: colors.backgroundTertiary }}>
        <Box>
          <Typography variant="body2" fontWeight={700} color={colors.textPrimary}>Grand total</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {stats.grandTotal.count} txn{stats.grandTotal.count !== 1 ? 's' : ''}
            {stats.grandTotal.tax > 0 && ` · Tax ${formatINR(stats.grandTotal.tax)}`}
          </Typography>
        </Box>
        <Typography variant="h6" fontWeight={800} color={colors.primary}>
          {formatINR(stats.grandTotal.netRevenue)}
        </Typography>
      </Box>
    </Box>
  );
};

export const RevenueStatCardWithBreakdown: React.FC<{
  stats: TransactionStats | null;
  periodLabel: string;
  isLoading?: boolean;
  value: string;
  onSegmentClick?: (sel: SegmentSelection) => void;
}> = ({ stats, periodLabel, isLoading, value, onSegmentClick }) => (
  <HoverPopover
    anchor={
      <StatCard
        label="Total Revenue"
        value={value}
        icon={CurrencyRupee}
        footer={
          <Typography variant="caption" color={colors.primary} sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
            Hover for breakdown
          </Typography>
        }
      />
    }
  >
    <RevenueBreakdownPopup stats={stats} periodLabel={periodLabel} isLoading={isLoading} onSegmentClick={onSegmentClick} />
  </HoverPopover>
);

// Money EXCLUDED from a card's total because it paid for a booking that was
// later cancelled with no refund on record — disclosed, not netted in, so a
// viewer comparing against the raw Transactions list can see why the two
// differ. Only rendered when there's something to disclose. `showAmount`
// opts into the ₹ figure too — off by default (just the count) to keep the
// line short and plain alongside a card's other captions. `onClick`, when
// given, opens the actual list of cancelled bookings behind the count.
export const CancelledNote: React.FC<{ cancelled?: { gross: number; count: number }; showAmount?: boolean; onClick?: () => void }> = ({ cancelled, showAmount, onClick }) => {
  if (!cancelled || cancelled.gross <= 0) return null;
  // Only the count itself is the drill-down link — same "underline just the
  // number, not the surrounding words" convention TxnCountText uses elsewhere.
  const countText = `${cancelled.count} cancelled`;
  const link = onClick ? (
    <Box
      component="span" onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      sx={{ cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
    >
      {countText}
    </Box>
  ) : countText;
  return (
    <Typography variant="caption" color="text.secondary" display="block">
      {showAmount ? <>excl. {formatINR(cancelled.gross)} · {link}</> : link}
    </Typography>
  );
};

const StudioRevenueStatCard: React.FC<{
  studioStat: StudioStat | undefined;
  studioLabel: string;
}> = ({ studioStat, studioLabel }) => (
  <StatCard
    label={`Total Revenue (${studioLabel})`}
    value={formatINR(studioStat?.totalNet ?? 0)}
    icon={CurrencyRupee}
    footer={
      studioStat ? (
        <GatewaySourceRows
          studioLabel={studioLabel}
          ownNet={studioStat.ownGatewayNet}
          sweatSocialNet={studioStat.sweatSocialGatewayNet}
        />
      ) : undefined
    }
  />
);

const SweatSocialRevenueStatCard: React.FC<{
  netRevenue: number;
  bySource?: SourceStat[];
}> = ({ netRevenue, bySource }) => (
  <StatCard
    label="Total Revenue (Sweat Social)"
    value={formatINR(netRevenue)}
    icon={CurrencyRupee}
    footer={<SweatSocialSourceRows bySource={bySource} />}
  />
);

// Cash / manually-recorded payments for whichever tab is active — kept as its
// own card, separate from the Razorpay-only cards above, so the two can't be
// confused. Backend-computed, period-wide (see the `cashManualTotal` comment
// at its call site).
const CashManualStatCard: React.FC<{ total: number; count: number; footer?: React.ReactNode }> = ({ total, count, footer }) => (
  <Card sx={{ backgroundColor: colors.white, border: `1px solid ${colors.warning}55`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box flex={1} minWidth={0}>
          <Typography variant="body2" color="text.secondary" mb={0.5}>Cash / Manual</Typography>
          <Typography variant="h4" fontWeight={700} color={colors.textPrimary}>
            ₹{total.toLocaleString('en-IN')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {count} cash / manual {count === 1 ? 'entry' : 'entries'}
          </Typography>
          {footer}
        </Box>
        <LocalAtm sx={{ fontSize: 48, color: colors.warning, opacity: 0.4, flexShrink: 0 }} />
      </Box>
    </CardContent>
  </Card>
);

// Same layout as RevenueBreakdownPopup, but for the cash / manual total instead
// of Razorpay revenue: per whitelabel studio, the studio's own-app cash vs cash
// booked through Sweat Social's own app/website for that studio's classes, then
// Sweat Social's own direct cash. Only rendered on the All tab.
const CashManualBreakdownPopup: React.FC<{
  cashManual: { total: number; count: number; breakdown?: CashManualBreakdown };
  periodLabel: string;
  isLoading?: boolean;
  onSegmentClick?: (sel: SegmentSelection) => void;
}> = ({ cashManual, periodLabel, isLoading, onSegmentClick }) => {
  const breakdown = cashManual.breakdown;

  if (isLoading && !breakdown) {
    return (
      <Box sx={{ width: POPUP_WIDTH, p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const studios = breakdown?.studios ?? [];
  const ssCash = breakdown?.sweatSocial?.cashRevenue ?? 0;
  const ssCashCount = breakdown?.sweatSocial?.cashCount ?? 0;
  const ssCashBySource = breakdown?.sweatSocial?.bySource ?? [];
  const hasStudios = studios.length > 0;
  const hasSweatSocial = ssCash > 0 || ssCashCount > 0;

  if (!breakdown || (!hasStudios && !hasSweatSocial)) {
    return (
      <Box sx={{ width: POPUP_WIDTH, p: 2.5 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={0.5}>Manual breakdown</Typography>
        <Typography variant="body2" color="text.secondary">No manual entries in this period</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: POPUP_WIDTH, maxWidth: '92vw' }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: `${colors.warning}14`, borderBottom: `1px solid ${colors.divider}` }}>
        <Typography variant="subtitle2" fontWeight={700} color={colors.textPrimary} lineHeight={1.3}>
          Manual breakdown
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
          {periodLabel} · Manual only
        </Typography>
      </Box>

      <Box sx={{ px: 2, py: 1.25, maxHeight: POPUP_BODY_MAX_HEIGHT, overflowY: 'auto' }}>
        {hasStudios && (
          <>
            {/* Same reasoning as RevenueBreakdownPopup's "Whitelabel studios"
                heading — a studio owner viewing their own Cash/Manual card
                only ever has one row here. */}
            {studios.length > 1 && (
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.6, textTransform: 'uppercase' }}>
                By studio
              </Typography>
            )}
            {studios.map((s, idx) => {
              const name = s.studioName || (s.source ? sourceLabel(s.source) : 'Studio');
              return (
                <Box key={s.studioId || s.source || idx} sx={{ py: 1.25, borderBottom: idx < studios.length - 1 || hasSweatSocial ? `1px solid ${colors.divider}` : 'none' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.5}>
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={700} color={colors.textPrimary} noWrap title={name}>
                        {name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.totalCashCount} entr{s.totalCashCount !== 1 ? 'ies' : 'y'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} color={colors.success} sx={{ flexShrink: 0 }}>
                      {formatINR(s.totalCashRevenue)}
                    </Typography>
                  </Box>
                  <GatewaySplitBar
                    ownLabel={name}
                    ownNet={s.ownCashRevenue}
                    sweatSocialNet={s.ssRoutedCashRevenue}
                    ownCount={s.ownCashCount}
                    sweatSocialCount={s.ssRoutedCashCount}
                    compact
                    onOwnClick={
                      onSegmentClick && s.ownCashCount > 0
                        ? () =>
                            onSegmentClick(
                              s.source
                                ? { source: s.source, studioName: name, bucket: 'partnerInApp', bucketLabel: 'In-app manual', count: s.ownCashCount }
                                : { studioId: s.studioId!, studioName: name, bucket: 'ownAppCash', bucketLabel: 'In-app manual', count: s.ownCashCount },
                            )
                        : undefined
                    }
                    onSweatSocialClick={
                      onSegmentClick && s.ssRoutedCashCount > 0
                        ? () =>
                            onSegmentClick(
                              s.source
                                ? { source: s.source, studioName: name, bucket: 'partnerViaSweatSocial', bucketLabel: 'Sweat Social-routed manual', count: s.ssRoutedCashCount }
                                : { studioId: s.studioId!, studioName: name, bucket: 'ssRoutedCash', bucketLabel: 'Sweat Social-routed manual', count: s.ssRoutedCashCount },
                            )
                        : undefined
                    }
                  />
                </Box>
              );
            })}
          </>
        )}

        {hasSweatSocial && (
          <Box sx={{ py: 1.25 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.6, textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
              Sweat Social (direct)
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box>
                <Typography variant="body2" fontWeight={600} color={colors.textPrimary}>All channels</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {/* The entry count is the drill-down link — the cash / manual
                      rows behind this figure. */}
                  <TxnCountText
                    count={ssCashCount}
                    separator=""
                    unit={['entry', 'entries']}
                    onClick={
                      onSegmentClick
                        ? () =>
                            onSegmentClick({
                              studioName: 'Sweat Social (direct)',
                              bucket: 'sweatSocialDirectCash',
                              bucketLabel: 'All channels (manual)',
                              count: ssCashCount,
                            })
                        : undefined
                    }
                  />
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={700} color={colors.success} sx={{ flexShrink: 0 }}>
                {formatINR(ssCash)}
              </Typography>
            </Box>
            {ssCashBySource.length > 0 && (
              <Box sx={{ pl: 1, borderLeft: `2px solid ${colors.warning}40`, mt: 0.75 }}>
                <SweatSocialSourceRows
                  bySource={ssCashBySource.map((r) => ({ source: r.source, netRevenue: r.cashRevenue, tax: 0, count: r.cashCount }))}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: colors.backgroundTertiary }}>
        <Box>
          <Typography variant="body2" fontWeight={700} color={colors.textPrimary}>Grand total</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {cashManual.count} entr{cashManual.count !== 1 ? 'ies' : 'y'}
          </Typography>
        </Box>
        <Typography variant="h6" fontWeight={800} color={colors.warning}>
          {formatINR(cashManual.total)}
        </Typography>
      </Box>
    </Box>
  );
};

// All-tab Cash/Manual card: same "hover for the per-studio division" affordance
// the Total Revenue card has. Every other tab keeps the plain CashManualStatCard.
const CashManualStatCardWithBreakdown: React.FC<{
  cashManual: { total: number; count: number; breakdown?: CashManualBreakdown };
  periodLabel: string;
  isLoading?: boolean;
  onSegmentClick?: (sel: SegmentSelection) => void;
}> = ({ cashManual, periodLabel, isLoading, onSegmentClick }) => (
  <HoverPopover
    anchor={
      <CashManualStatCard
        total={cashManual.total}
        count={cashManual.count}
        footer={
          <Typography variant="caption" color={colors.warning} sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
            Hover for breakdown
          </Typography>
        }
      />
    }
  >
    <CashManualBreakdownPopup cashManual={cashManual} periodLabel={periodLabel} isLoading={isLoading} onSegmentClick={onSegmentClick} />
  </HoverPopover>
);

const SEGMENT_PAGE_SIZE = 25;

// Read-only transaction card for the drill-down dialog — the same fields the
// Transactions list card shows, minus the row actions.
export const SegmentTransactionCard: React.FC<{ t: Transaction }> = ({ t }) => {
  const gst = resolveGst(t);
  const studioName =
    t.purchasedPack?.studio?.studioName ||
    t.purchasedPack?.studioName ||
    t.booking?.studio?.studioName ||
    'N/A';
  const isSS = isSweatSocialTransaction(t);
  return (
    <Card sx={{ border: `1px solid ${colors.divider}`, borderRadius: 2 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {t.purchasedPack
                ? t.purchasedPack.packTitle || t.purchasedPack.title || 'Pack Purchase'
                : t.booking?.event?.eventName || t.booking?.session?.className || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t.purchasedPack ? 'Pack Purchase' : t.booking?.bookingId || 'No Booking ID'}
            </Typography>
          </Box>
          <Box textAlign="right" flexShrink={0}>
            <Typography variant="body2" fontWeight={700} color={colors.success}>
              ₹{(t.basePrice + t.taxes).toLocaleString('en-IN')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(t.createdAt).toLocaleDateString('en-IN')}{' '}
              {new Date(t.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2} mt={1.25}>
          <Box minWidth={0}>
            <Typography variant="caption" color="text.secondary" display="block">User</Typography>
            <Typography variant="body2" fontWeight={500} noWrap>{t.user?.name || 'N/A'}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>{t.user?.email || ''}</Typography>
          </Box>
          <Box minWidth={0}>
            <Typography variant="caption" color="text.secondary" display="block">Studio</Typography>
            <Typography variant="body2" fontWeight={500} noWrap>{studioName}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Payment Mode</Typography>
            <Chip
              label={(t.modeOfPayment || '').toUpperCase()}
              size="small"
              sx={{
                backgroundColor: `${getPaymentModeColor(t.modeOfPayment)}15`,
                color: getPaymentModeColor(t.modeOfPayment),
                border: `1px solid ${getPaymentModeColor(t.modeOfPayment)}30`,
                fontWeight: 600,
              }}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Source</Typography>
            <Chip
              label={sourceLabel(effectiveTransactionSource(t))}
              size="small"
              sx={{
                backgroundColor: isSS ? `${colors.primary}15` : `${colors.warning}15`,
                color: isSS ? colors.primary : colors.warning,
                border: `1px solid ${isSS ? colors.primary : colors.warning}30`,
                fontWeight: 600,
              }}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Base + Tax</Typography>
            <Typography variant="body2">
              ₹{gst.base.toLocaleString('en-IN', { maximumFractionDigits: 2 })} + ₹{gst.tax.toFixed(2)}
            </Typography>
          </Box>
          {t.paymentId && (
            <Box minWidth={0}>
              <Typography variant="caption" color="text.secondary" display="block">Payment ID</Typography>
              <Typography variant="body2" noWrap>{t.paymentId}</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Drill-down dialog: the actual transactions behind a clicked "· N txns"
// split-bar segment. Fetches the dedicated stats-segment endpoint so the rows
// match that segment's classification (and count) exactly.
export const SegmentTransactionsDialog: React.FC<{
  open: boolean;
  selection: SegmentSelection | null;
  startDate?: string;
  endDate?: string;
  periodLabel: string;
  onClose: () => void;
}> = ({ open, selection, startDate, endDate, periodLabel, onClose }) => {
  const [rows, setRows] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const fetchIdRef = useRef(0);

  const studioId = selection?.studioId;
  const source = selection?.source;
  const bucket = selection?.bucket;

  const loadPage = useCallback(
    (p: number) => {
      // The sweatSocialDirect* buckets are global — they need neither studioId
      // nor source (source only narrows them to one channel). studioRazorpayAll/
      // studioCashAll (Dashboard's merged-row buckets) don't strictly need one
      // either — the studio-scoped backend route always forces the caller's own
      // studioId server-side regardless of what's sent — but without listing
      // them here, this guard returned early and never called the API at all,
      // which is why the dialog always showed "No transactions" for them.
      const isGlobalBucket =
        bucket === 'sweatSocialDirect' ||
        bucket === 'sweatSocialDirectCash' ||
        bucket === 'cancelled' ||
        bucket === 'studioRazorpayAll' ||
        bucket === 'studioCashAll' ||
        bucket === 'allRazorpay' ||
        bucket === 'allCash';
      if (!bucket || (!isGlobalBucket && !studioId && !source)) return;
      const fetchId = ++fetchIdRef.current;
      setLoading(true);
      setErr(null);
      apiService
        .getStatsSegmentTransactions({
          studioId,
          source,
          bucket,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          page: p,
          pageSize: SEGMENT_PAGE_SIZE,
        })
        .then((res: any) => {
          if (fetchId !== fetchIdRef.current) return;
          setRows(res.transactions || []);
          setPages(res.pages || 1);
          setCount(res.count || 0);
          setPage(p);
        })
        .catch((e: any) => {
          if (fetchId !== fetchIdRef.current) return;
          setErr(e.message || 'Failed to load transactions');
        })
        .finally(() => {
          if (fetchId === fetchIdRef.current) setLoading(false);
        });
    },
    [studioId, source, bucket, startDate, endDate],
  );

  useEffect(() => {
    if (!open) return;
    loadPage(1);
  }, [open, loadPage]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {selection ? `${selection.studioName} — ${selection.bucketLabel}` : 'Transactions'}
        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
          {periodLabel} · {count} transaction{count !== 1 ? 's' : ''}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box py={5} textAlign="center">
            <Typography color="text.secondary">No transactions</Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            {rows.map((t) => <SegmentTransactionCard key={t._id} t={t} />)}
            {pages > 1 && (
              <Box display="flex" justifyContent="center" pt={1}>
                <Pagination
                  count={pages}
                  page={page}
                  onChange={(_, p) => { if (p !== page) loadPage(p); }}
                  color="primary"
                  size="small"
                  disabled={loading}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SUPPORT_PHONE = '+91 9606464667';

// What an owner gets when trying to edit a Razorpay payment.
const ContactSupportDialog: React.FC<{ transaction: Transaction | null; onClose: () => void }> = ({
  transaction,
  onClose,
}) => (
  <Dialog open={Boolean(transaction)} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 700 }}>Contact customer support</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary">
        {transaction?.isReceivedBySweatSocial
          ? "This payment was received by Sweat Social, so it can't be edited here. Please reach out to customer support to change it."
          : "This payment was made through Razorpay, so it can't be edited here. Please reach out to customer support to change it."}
      </Typography>
      <Typography variant="body1" fontWeight={700} mt={1.5}>
        <a href={`tel:${SUPPORT_PHONE.replace(/\s/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}>
          {SUPPORT_PHONE}
        </a>
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5 }}>
      <Button variant="contained" onClick={onClose}>OK</Button>
    </DialogActions>
  </Dialog>
);

const CancelInvoiceDialog: React.FC<{
  transaction: Transaction | null;
  onClose: () => void;
  onCancelled: () => void;
}> = ({ transaction, onClose, onCancelled }) => {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setErr(null);
  }, [transaction]);

  const confirm = async () => {
    if (!transaction) return;
    try {
      setSaving(true);
      setErr(null);
      await apiService.cancelInvoice(transaction._id, transactionStudioId(transaction));
      onCancelled();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e.message || 'Failed to cancel invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(transaction)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cancel invoice?</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Typography variant="body2" color="text.secondary">
          {transaction
            ? transaction.isPaymentPending
              ? `The member will no longer owe ${formatINR(transaction.basePrice + transaction.taxes)}. This can't be undone.`
              : `${formatINR(transaction.basePrice + transaction.taxes)} will be removed from revenue. This can't be undone.`
            : ''}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Keep</Button>
        <Button variant="contained" color="error" onClick={confirm} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Cancel invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditTransactionDialog: React.FC<{
  transaction: Transaction | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ transaction, onClose, onSaved }) => {
  type EditableMode = 'cash' | 'online';
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState<EditableMode>('cash');
  const [reviewing, setReviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const currentAmount = transaction
    ? Math.round((transaction.basePrice + transaction.taxes) * 100) / 100
    : 0;

  useEffect(() => {
    if (!transaction) return;
    setAmount(String(currentAmount));
    setMode(transaction.modeOfPayment as EditableMode);
    setReviewing(false);
    setErr(null);
  }, [transaction, currentAmount]);

  const newAmount = Math.round(Number(amount) * 100) / 100;
  const amountValid = amount.trim() !== '' && Number.isFinite(newAmount) && newAmount >= 1;
  const amountChanged = newAmount !== currentAmount;
  const modeChanged = mode !== transaction?.modeOfPayment;
  const gst = transaction && amountValid
    ? resolveGst({ ...transaction, basePrice: newAmount, taxes: 0 })
    : null;

  // Plain text, not type="number": a number field changes its value on the
  // scroll wheel and arrow keys, which is no way to handle someone's money.
  const handleAmountChange = (raw: string) => {
    const cleaned = raw.replace(/,/g, '');
    if (/^\d{0,7}(\.\d{0,2})?$/.test(cleaned)) setAmount(cleaned);
  };

  const save = async () => {
    if (!transaction) return;
    try {
      setSaving(true);
      setErr(null);
      await apiService.updateTransactionPayment(
        transaction._id,
        {
          ...(amountChanged ? { amount: newAmount } : {}),
          ...(modeChanged ? { modeOfPayment: mode } : {}),
        },
        transactionStudioId(transaction),
      );
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e.message || 'Failed to update transaction');
    } finally {
      setSaving(false);
    }
  };

  const itemName = transaction?.purchasedPack
    ? transaction.purchasedPack.packTitle || transaction.purchasedPack.title || 'Pack Purchase'
    : transaction?.booking?.event?.eventName || transaction?.booking?.session?.className || 'N/A';

  const reviewRow = (label: string, from: string, to: string) => (
    <Box
      display="grid"
      gridTemplateColumns="1fr 1fr 1fr"
      gap={1}
      py={1.25}
      sx={{ borderTop: `1px solid ${colors.divider}` }}
    >
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" color="text.secondary">{from}</Typography>
      <Typography variant="body2" fontWeight={700}>{to}</Typography>
    </Box>
  );

  return (
    // Backdrop clicks are ignored so a stray tap can't lose or skip anything here.
    <Dialog
      open={Boolean(transaction)}
      onClose={(_, reason) => { if (reason !== 'backdropClick' && !saving) onClose(); }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{reviewing ? 'Confirm payment change' : 'Edit payment'}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

        <Box sx={{ p: 1.5, mb: 2.5, borderRadius: 2, backgroundColor: colors.backgroundTertiary }}>
          <Typography variant="body2" fontWeight={600}>{transaction?.user?.name || 'N/A'}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">{itemName}</Typography>
          {transaction && (
            <Typography variant="caption" color="text.secondary" display="block">
              {new Date(transaction.createdAt).toLocaleDateString('en-IN')}
            </Typography>
          )}
        </Box>

        {reviewing ? (
          <>
            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={1} pb={0.75}>
              <span />
              <Typography variant="caption" color="text.secondary">Current</Typography>
              <Typography variant="caption" color="text.secondary">New</Typography>
            </Box>
            {amountChanged && reviewRow('Amount', formatINR(currentAmount), formatINR(newAmount))}
            {modeChanged && reviewRow(
              'Mode',
              (transaction?.modeOfPayment || '').toUpperCase(),
              mode.toUpperCase(),
            )}
            <Typography variant="body2" color="text.secondary" mt={2}>
              The booking and invoice will show these values.
            </Typography>
            {transaction && isRazorpayPayment(transaction) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This payment came through Razorpay. This change also affects the studio's payout and any refund.
              </Alert>
            )}
          </>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={2}>
              <Typography variant="body2" color="text.secondary">Current</Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatINR(currentAmount)} · {(transaction?.modeOfPayment || '').toUpperCase()}
              </Typography>
            </Box>

            <Typography variant="body2" fontWeight={600} mb={0.75}>Payment mode</Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              color="primary"
              value={mode}
              onChange={(_, v) => { if (v) setMode(v); }}
              sx={{ mb: 2.5 }}
            >
              <ToggleButton value="cash">Cash</ToggleButton>
              <ToggleButton value="online">Online</ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="body2" fontWeight={600} mb={0.75}>New amount</Typography>
            <TextField
              fullWidth
              type="text"
              autoComplete="off"
              value={amount}
              onChange={(e) => handleAmountChange(e.target.value)}
              slotProps={{
                htmlInput: { inputMode: 'decimal', 'aria-label': 'New amount', style: { fontSize: '1.4rem', fontWeight: 600 } },
                input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> },
              }}
            />
            <Typography variant="caption" color="text.secondary" display="block" mt={0.75} minHeight={18}>
              {gst
                ? `Base ${formatINR(gst.base)} + Tax ${formatINR(gst.tax)}`
                : amount.trim() !== '' ? 'Must be at least ₹1' : ''}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {reviewing ? (
          <>
            <Button onClick={() => setReviewing(false)} disabled={saving}>Back</Button>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? <CircularProgress size={18} /> : 'Confirm change'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => setReviewing(true)}
              disabled={!amountValid || (!amountChanged && !modeChanged)}
            >
              Review
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const isStudio = isStudioScopedRole(admin?.role);
  const isManager = admin?.role === 'manager';
  const isSuperAdmin = admin?.role === 'super_admin';
  // One page (PAGE_SIZE rows) of the currently selected month/range — the list
  // paginates within the period, but the stats cards below come from a separate
  // backend aggregate scoped to the WHOLE period, not just this page.
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // searchInput updates on every keystroke (for a responsive text field);
  // searchTerm is the debounced value actually sent to the server.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentModeFilter, setPaymentModeFilter] = useState('all');
  const [sourceTab, setSourceTab] = useState<string>('all');
  const [studios, setStudios] = useState<Studio[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [cancellingTransaction, setCancellingTransaction] = useState<Transaction | null>(null);
  const [supportTransaction, setSupportTransaction] = useState<Transaction | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // True only until the very first fetch resolves. Every fetch (mount, tab
  // switch, search, page change) sets isLoading, but only the FIRST one should
  // blank the whole page — after that, the page shell (search box, tabs, stats)
  // should stay put and just the list should show it's refreshing.
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  // Which tab totalCount/transactions actually belong to — same reasoning as the
  // Bookings page: a tab switch clears the old page before the new one arrives,
  // so without this the badge briefly shows the PREVIOUS tab's number.
  const [countLoadedForTab, setCountLoadedForTab] = useState<string>('all');

  // Date filter: a month picker (defaulting to the current month-to-date), or a
  // custom range. Drives both the row list and the stats aggregate.
  const [filterMode, setFilterMode] = useState<'month' | 'custom'>('month');
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonthKey());
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const [stats, setStats] = useState<TransactionStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  const fetchIdRef = useRef(0);
  const statsFetchIdRef = useRef(0);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Resolves the filter UI state into concrete IST-boundary ISO strings.
  const getDateRange = (): { startDate: string; endDate: string } => {
    if (filterMode === 'custom') {
      const start = customStartDate ? toISTStart(...(customStartDate.split('-') as [string, string, string])) : '';
      const end = customEndDate ? toISTEnd(...(customEndDate.split('-') as [string, string, string])) : '';
      return { startDate: start, endDate: end };
    }
    return computeMonthRange(selectedMonth);
  };

  const goToPreviousMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const prev = new Date(year, month - 2, 1);
    const newMonth = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(newMonth);
    void refetchAll(1, undefined, undefined, undefined, newMonth);
  };

  const goToNextMonth = () => {
    if (selectedMonth >= getCurrentMonthKey()) return; // can't page past the current month
    const [year, month] = selectedMonth.split('-').map(Number);
    const next = new Date(year, month, 1);
    const newMonth = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(newMonth);
    void refetchAll(1, undefined, undefined, undefined, newMonth);
  };

  const applyCustomRange = () => {
    if (!customStartDate || !customEndDate) return;
    void refetchAll(1);
  };

  useEffect(() => {
    if (!isStudio) {
      fetchStudios();
    }
    fetchTransactions(1);
    fetchStats();
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStudios = async () => {
    try {
      const response = await apiService.getStudios();
      const studiosData = response.data || response.studios || [];
      setStudios(Array.isArray(studiosData) ? studiosData : []);
    } catch (err) {
      console.error('Error fetching studios:', err);
      setStudios([]);
    }
  };

  const fetchStats = async (sourceTabOverride?: string, monthOverride?: string) => {
    const fetchId = ++statsFetchIdRef.current;
    try {
      setIsLoadingStats(true);
      const effectiveSourceTab = sourceTabOverride !== undefined ? sourceTabOverride : sourceTab;
      const range = monthOverride !== undefined ? computeMonthRange(monthOverride) : getDateRange();
      if (!range.startDate && !range.endDate) return;

      const params: any = { startDate: range.startDate, endDate: range.endDate };
      // A specific whitelabel-studio tab scopes the stats to just that studio
      // (own vs Sweat Social gateway split, no separate Sweat Social bucket).
      if (effectiveSourceTab !== 'all' && effectiveSourceTab !== 'sweatsocial') {
        const tabStudio = studios.find(s => s.whitelabelConfig?.appSource === effectiveSourceTab);
        if (tabStudio?._id) params.studioId = tabStudio._id;
      }

      const response = await apiService.getTransactionStats(params);
      if (fetchId !== statsFetchIdRef.current) return;
      setStats(response);
    } catch (err) {
      if (fetchId !== statsFetchIdRef.current) return;
      console.error('Error fetching transaction stats:', err);
      setStats(null);
    } finally {
      if (fetchId === statsFetchIdRef.current) setIsLoadingStats(false);
    }
  };

  // Refetches both the row list and the stats together — used whenever the
  // date range itself changes (month nav, custom range apply), since both are
  // scoped to it.
  const refetchAll = async (
    page: number,
    searchOverride?: string,
    paymentModeOverride?: string,
    sourceTabOverride?: string,
    monthOverride?: string,
  ) => {
    await Promise.all([
      fetchTransactions(page, searchOverride, paymentModeOverride, sourceTabOverride, monthOverride),
      fetchStats(sourceTabOverride, monthOverride),
    ]);
  };

  const fetchTransactions = async (
    page: number,
    searchOverride?: string,
    paymentModeOverride?: string,
    sourceTabOverride?: string,
    monthOverride?: string,
  ) => {
    const fetchId = ++fetchIdRef.current;
    try {
      setIsLoading(true);
      setError(null);

      const effectiveSearch = searchOverride !== undefined ? searchOverride : searchTerm;
      const effectivePaymentMode = paymentModeOverride !== undefined ? paymentModeOverride : paymentModeFilter;
      const effectiveSourceTab = sourceTabOverride !== undefined ? sourceTabOverride : sourceTab;
      const range = monthOverride !== undefined ? computeMonthRange(monthOverride) : getDateRange();

      // This page's own display rule (pack deductions/free bookings aren't
      // "payments" for a revenue view) — opt-in only, since getAllTransactions
      // is shared with the User Detail page's Transaction History tab, which
      // needs those rows shown, not hidden.
      const params: any = { page, pageSize: PAGE_SIZE, sort: '-1', excludePackAndFree: 'true' };
      if (effectiveSearch.trim()) params.search = effectiveSearch.trim();
      if (effectivePaymentMode !== 'all') params.modeOfPayment = effectivePaymentMode;
      if (effectiveSourceTab !== 'all') params.source = effectiveSourceTab;
      if (range.startDate) params.startDate = range.startDate;
      if (range.endDate) params.endDate = range.endDate;

      const response = await apiService.getTransactions(params);
      // A newer fetch (another tab/filter/page click) has already started —
      // this response is stale, discard it instead of overwriting fresher state.
      if (fetchId !== fetchIdRef.current) return;

      setTransactions(response.transactions || []);
      setTotalPages(response.pages || 1);
      setTotalCount(response.count || 0);
      setCurrentPage(page);
      setCountLoadedForTab(effectiveSourceTab);
    } catch (err: any) {
      if (fetchId !== fetchIdRef.current) return;
      setError(err.message || 'Failed to fetch transactions');
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false);
        setIsInitialLoading(false);
      }
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(value);
      fetchTransactions(1, value);
    }, 400);
  };

  const handlePaymentModeFilter = (event: any) => {
    setPaymentModeFilter(event.target.value);
    fetchTransactions(1, undefined, event.target.value);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, transaction: Transaction) => {
    setAnchorEl(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTransaction(null);
  };

  const handleViewDetails = () => {
    if (selectedTransaction?.booking?._id) {
      navigate(`/bookings/${selectedTransaction.booking._id}`);
    }
    handleMenuClose();
  };

  const handleDownloadReceipt = async (transaction: Transaction) => {
    try {
      setDownloadingId(transaction._id);
      const blob = await apiService.downloadTransactionReceipt(transaction._id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const label = transaction.purchasedPack
        ? (transaction.purchasedPack.packTitle || transaction.purchasedPack.title || 'pack')
        : (transaction.booking?.bookingId || transaction._id);
      a.download = `Receipt_${label}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download receipt');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewReceipt = async (transaction: Transaction) => {
    try {
      setViewingId(transaction._id);
      const blob = await apiService.downloadTransactionReceipt(transaction._id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke after a short delay to allow the tab to load the PDF
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err: any) {
      setError(err.message || 'Failed to view receipt');
    } finally {
      setViewingId(null);
    }
  };

  const handleMenuDownloadReceipt = async () => {
    if (selectedTransaction) {
      handleMenuClose();
      await handleDownloadReceipt(selectedTransaction);
    }
  };

  const handleMenuViewReceipt = async () => {
    if (selectedTransaction) {
      handleMenuClose();
      await handleViewReceipt(selectedTransaction);
    }
  };

  // Whitelabel source tabs, built from the studios list so every tab is always
  // visible — not just sources present in the current (25-row) page. Falls back
  // to deriving from the loaded page if studios hasn't loaded yet, same as the
  // Bookings page.
  const whitelabelSources = useMemo(() => {
    if (studios.length > 0) {
      const seen = new Set<string>();
      studios.forEach(s => {
        const src = s.whitelabelConfig?.appSource;
        if (src && src !== 'mobile_app' && src !== 'website' && src !== 'admin') seen.add(src);
      });
      return Array.from(seen);
    }
    const seen = new Set<string>();
    transactions.forEach(t => {
      const src = effectiveTransactionSource(t);
      if (src && src !== 'mobile_app' && src !== 'sweatsocial' && src !== 'website' && src !== 'admin') seen.add(src);
    });
    return Array.from(seen);
  }, [studios, transactions]);

  // Search/payment-mode/source filtering happens server-side (see
  // fetchTransactions) — `transactions` is already exactly the current page's
  // matching rows. The stats cards below come from a separate backend
  // aggregate (`stats`) scoped to the whole selected period.

  // On a whitelabel-studio tab, `stats.studios` has at most the one requested
  // studio's row (the backend scopes it via studioId).
  const activeStudioStat: StudioStat | undefined = stats?.studios?.[0];

  // Cash / manually-recorded payments — backend-computed, period-wide (see
  // TransactionStats.cashManual). The Sweat Social tab has its own scoped
  // figure (stats.sweatSocial.cashRevenue/cashCount); the All tab and every
  // whitelabel-studio tab share stats.cashManual, which the backend already
  // scopes correctly for each (global sum vs one studio's total).
  const cashManualTotal = sourceTab === 'sweatsocial' ? (stats?.sweatSocial?.cashRevenue ?? 0) : (stats?.cashManual?.total ?? 0);
  const cashManualCount = sourceTab === 'sweatsocial' ? (stats?.sweatSocial?.cashCount ?? 0) : (stats?.cashManual?.count ?? 0);

  const statsPeriodLabel =
    filterMode === 'month'
      ? monthLabel(selectedMonth)
      : `${customStartDate || '…'} → ${customEndDate || '…'}`;

  // Drill-down: which split-bar segment (studio × bucket) the user clicked.
  const [segmentSel, setSegmentSel] = useState<SegmentSelection | null>(null);
  const segmentRange = getDateRange();

  if (isInitialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700} mb={1}>
          Transactions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage and view all transaction records
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Source Tabs — super admin only. `transactions` only ever holds the
          current page for whichever ONE tab was last fetched, so it can't give
          an accurate count for every tab at once — only the active tab (once
          countLoadedForTab catches up to it) shows the real, backend-computed
          total; the rest just show their name. Same reasoning as Bookings. */}
      {!isStudio && (
        <Tabs
          value={sourceTab}
          onChange={(_, v) => { setSourceTab(v); fetchTransactions(1, undefined, undefined, v); fetchStats(v); }}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={sourceTab === 'all' && countLoadedForTab === 'all' ? `All (${totalCount})` : 'All'}
            value="all"
          />
          <Tab
            label={
              sourceTab === 'sweatsocial' && countLoadedForTab === 'sweatsocial'
                ? `Sweat Social (${totalCount})`
                : 'Sweat Social'
            }
            value="sweatsocial"
          />
          {whitelabelSources.map(src => (
            <Tab
              key={src}
              label={
                sourceTab === src && countLoadedForTab === src
                  ? `${sourceLabel(src)} (${totalCount})`
                  : sourceLabel(src)
              }
              value={src}
            />
          ))}
        </Tabs>
      )}

      {/* Date filter: a month picker (default: this month so far), or a custom
          range — drives both the row list below and the stats cards. */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <ToggleButtonGroup
              size="small"
              value={filterMode}
              exclusive
              onChange={(_, v) => {
                if (!v) return;
                setFilterMode(v);
                if (v === 'month') {
                  void refetchAll(1, undefined, undefined, undefined, selectedMonth);
                }
              }}
            >
              <ToggleButton value="month">Month</ToggleButton>
              <ToggleButton value="custom">Custom Range</ToggleButton>
            </ToggleButtonGroup>

            {filterMode === 'month' ? (
              <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton size="small" onClick={goToPreviousMonth} title="Previous month">
                  <ChevronLeft />
                </IconButton>
                <Typography variant="body1" fontWeight={600} sx={{ minWidth: 170, textAlign: 'center' }}>
                  {monthLabel(selectedMonth)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={goToNextMonth}
                  disabled={selectedMonth >= getCurrentMonthKey()}
                  title="Next month"
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  type="date"
                  size="small"
                  label="From"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: getTodayISTDateKey() } }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="To"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: getTodayISTDateKey() } }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={applyCustomRange}
                  disabled={!customStartDate || !customEndDate}
                >
                  Apply
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Stats Cards — hidden for managers. From a backend aggregate scoped to
          the WHOLE selected period (see fetchStats). The revenue/tax/count
          cards are razorpay-only — pack redemptions and free transactions
          never enter them, and neither does cash (that's Cash/Manual's own
          number, tracked separately so the two can't be confused). */}
      {!isManager && (
        isLoadingStats && !stats ? (
          <Box display="flex" justifyContent="center" py={3} mb={3}><CircularProgress size={28} /></Box>
        ) : sourceTab === 'sweatsocial' ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
            <SweatSocialRevenueStatCard
              netRevenue={stats?.sweatSocial?.netRevenue ?? 0}
              bySource={stats?.sweatSocial?.bySource}
            />
            <StatCard
              label="Total Transactions (Sweat Social)"
              value={stats?.sweatSocial?.count ?? 0}
              icon={TrendingUp}
            />
            <CashManualStatCard total={cashManualTotal} count={cashManualCount} />
            <StatCard
              label="Total Taxes (Sweat Social)"
              value={formatINR(stats?.sweatSocial?.tax ?? 0)}
              icon={Receipt}
            />
          </Box>
        ) : sourceTab === 'all' ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
            <RevenueStatCardWithBreakdown
              stats={stats}
              isLoading={isLoadingStats}
              value={formatINR(stats?.grandTotal?.netRevenue ?? 0)}
              periodLabel={statsPeriodLabel}
              onSegmentClick={setSegmentSel}
            />
            <StatCard label="Total Transactions" value={stats?.grandTotal?.count ?? 0} icon={TrendingUp} />
            <CashManualStatCardWithBreakdown
              cashManual={{ total: cashManualTotal, count: cashManualCount, breakdown: stats?.cashManual?.breakdown }}
              isLoading={isLoadingStats}
              periodLabel={statsPeriodLabel}
              onSegmentClick={setSegmentSel}
            />
            <StatCard label="Total Taxes" value={formatINR(stats?.grandTotal?.tax ?? 0)} icon={Receipt} />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
            <StudioRevenueStatCard
              studioStat={activeStudioStat}
              studioLabel={activeStudioStat?.studioName || sourceLabel(sourceTab)}
            />
            <StatCard label="Total Transactions" value={activeStudioStat?.totalCount ?? 0} icon={TrendingUp} />
            <CashManualStatCard total={cashManualTotal} count={cashManualCount} />
            <StatCard label="Total Taxes" value={formatINR(activeStudioStat?.totalTax ?? 0)} icon={Receipt} />
          </Box>
        )
      )}

      {/* Search and Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              fullWidth
              placeholder="Search by user name, email, phone, booking ID, studio, event/session, pack title, or payment ID..."
              value={searchInput}
              onChange={handleSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: colors.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Payment Mode</InputLabel>
              <Select value={paymentModeFilter} label="Payment Mode" onChange={handlePaymentModeFilter}>
                <MenuItem value="all">All Modes</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                {/* Backend modeOfPayment enum is online | cash | subscription | free;
                    'Cash' covers the cash / manually-recorded entries. */}
                <MenuItem value="cash">Cash</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Box sx={{ position: 'relative' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1,
            borderRadius: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: isLoading ? 0.5 : 1 }}>
        {transactions.length === 0 ? (
          <Card>
            <CardContent sx={{ py: 4, textAlign: 'center' }}>
              <Receipt sx={{ fontSize: 64, color: colors.textSecondary, mb: 2, opacity: 0.3 }} />
              <Typography variant="body1" color="text.secondary">
                No transactions found
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {transactions.map((transaction) => (
              <Card
                key={transaction._id}
                sx={{
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 2,
                  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)', transition: 'all 0.2s ease' },
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Receipt sx={{ color: colors.primary, fontSize: 28 }} />
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {transaction.purchasedPack
                              ? transaction.purchasedPack.packTitle || transaction.purchasedPack.title || 'Pack Purchase'
                              : transaction.booking?.event?.eventName || transaction.booking?.session?.className || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {transaction.purchasedPack
                              ? 'Pack Purchase'
                              : transaction.booking?.bookingId || 'No Booking ID'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">User</Typography>
                          <Typography variant="body2" fontWeight={500}>{transaction.user?.name || 'N/A'}</Typography>
                          <Typography variant="caption" color="text.secondary">{transaction.user?.email || 'N/A'}</Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Studio</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {transaction.purchasedPack?.studio?.studioName ||
                              transaction.purchasedPack?.studioName ||
                              transaction.booking?.studio?.studioName ||
                              'N/A'}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Payment Mode</Typography>
                          <Chip
                            label={transaction.modeOfPayment.toUpperCase()}
                            size="small"
                            sx={{
                              backgroundColor: `${getPaymentModeColor(transaction.modeOfPayment)}15`,
                              color: getPaymentModeColor(transaction.modeOfPayment),
                              border: `1px solid ${getPaymentModeColor(transaction.modeOfPayment)}30`,
                              fontWeight: 600,
                            }}
                          />
                        </Box>

                        {!isStudio && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">Source</Typography>
                            <Chip
                              label={sourceLabel(effectiveTransactionSource(transaction))}
                              size="small"
                              sx={{
                                backgroundColor: isSweatSocialTransaction(transaction) ? `${colors.primary}15` : `${colors.warning}15`,
                                color: isSweatSocialTransaction(transaction) ? colors.primary : colors.warning,
                                border: isSweatSocialTransaction(transaction) ? `1px solid ${colors.primary}30` : `1px solid ${colors.warning}30`,
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        )}

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Amount</Typography>
                          <Typography
                            variant="body1"
                            fontWeight={700}
                            color={transaction.isInvoiceCancelled ? colors.textSecondary : colors.success}
                            sx={transaction.isInvoiceCancelled ? { textDecoration: 'line-through' } : undefined}
                          >
                            ₹{(transaction.basePrice + transaction.taxes).toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Base: ₹{resolveGst(transaction).base.toLocaleString('en-IN', { maximumFractionDigits: 2 })} + Tax: ₹{resolveGst(transaction).tax.toFixed(2)}
                          </Typography>
                          {!isStudio && transaction.originalAmount != null && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Original: {formatINR(transaction.originalAmount)}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Date</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {new Date(transaction.createdAt).toLocaleDateString('en-IN')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(transaction.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      {transaction.isInvoiceCancelled && (
                        <Chip
                          label="Cancelled"
                          size="small"
                          sx={{
                            backgroundColor: `${colors.textSecondary}15`,
                            color: colors.textSecondary,
                            border: `1px solid ${colors.textSecondary}30`,
                            fontWeight: 600,
                          }}
                        />
                      )}
                      {transaction.isPaymentPending && (
                        <Chip
                          label="Pending"
                          size="small"
                          sx={{
                            backgroundColor: `${colors.warning}15`,
                            color: colors.warning,
                            border: `1px solid ${colors.warning}30`,
                            fontWeight: 600,
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        title="View Receipt"
                        onClick={() => handleViewReceipt(transaction)}
                        disabled={viewingId === transaction._id}
                        sx={{ color: colors.primary }}
                      >
                        {viewingId === transaction._id
                          ? <CircularProgress size={18} />
                          : <Visibility fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Download Receipt"
                        onClick={() => handleDownloadReceipt(transaction)}
                        disabled={downloadingId === transaction._id}
                        sx={{ color: colors.primary }}
                      >
                        {downloadingId === transaction._id
                          ? <CircularProgress size={18} />
                          : <Download fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, transaction)}>
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => {
                    if (page !== currentPage) {
                      fetchTransactions(page);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  color="primary"
                  showFirstButton
                  showLastButton
                  disabled={isLoading}
                />
                <Typography variant="body2" color="text.secondary">
                  {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
      </Box>

      {/* Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewDetails} disabled={!selectedTransaction?.booking?._id}>
          <Receipt sx={{ fontSize: 20, mr: 1 }} /> View Booking Details
        </MenuItem>
        <MenuItem onClick={handleMenuViewReceipt}>
          <Visibility sx={{ fontSize: 20, mr: 1 }} /> View Receipt
        </MenuItem>
        <MenuItem onClick={handleMenuDownloadReceipt}>
          <Download sx={{ fontSize: 20, mr: 1 }} /> Download Receipt
        </MenuItem>
        {selectedTransaction &&
          (isSuperAdmin
            ? isLivePayment(selectedTransaction)
            : isTransactionEditable(selectedTransaction) ||
              (isLivePayment(selectedTransaction) && isRazorpayPayment(selectedTransaction))) && (
          <MenuItem
            onClick={() => {
              if (!isSuperAdmin && isRazorpayPayment(selectedTransaction)) {
                setSupportTransaction(selectedTransaction);
              } else {
                setEditingTransaction(selectedTransaction);
              }
              handleMenuClose();
            }}
          >
            <EditOutlined sx={{ fontSize: 20, mr: 1 }} /> Edit
          </MenuItem>
        )}
        {selectedTransaction && isInvoiceCancellable(selectedTransaction) && (
          <MenuItem
            onClick={() => {
              setCancellingTransaction(selectedTransaction);
              handleMenuClose();
            }}
          >
            <Block sx={{ fontSize: 20, mr: 1 }} /> Cancel Invoice
          </MenuItem>
        )}
      </Menu>

      <ContactSupportDialog
        transaction={supportTransaction}
        onClose={() => setSupportTransaction(null)}
      />

      <CancelInvoiceDialog
        transaction={cancellingTransaction}
        onClose={() => setCancellingTransaction(null)}
        onCancelled={() => {
          setCancellingTransaction(null);
          void refetchAll(currentPage);
        }}
      />

      <EditTransactionDialog
        transaction={editingTransaction}
        onClose={() => setEditingTransaction(null)}
        onSaved={() => {
          setEditingTransaction(null);
          void refetchAll(currentPage);
        }}
      />

      <SegmentTransactionsDialog
        open={Boolean(segmentSel)}
        selection={segmentSel}
        startDate={segmentRange.startDate}
        endDate={segmentRange.endDate}
        periodLabel={statsPeriodLabel}
        onClose={() => setSegmentSel(null)}
      />
    </Box>
  );
};

export default TransactionsPage;
